import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useToast } from "@/hooks/use-toast";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Standard-Rollen (nicht löschbar)
const SYSTEM_ROLE_IDS = ["admin", "trainer", "mitarbeiter"];

export function RolePermissionsMatrix({ authToken }) {
  const { toast } = useToast();
  const [permissions, setPermissions] = useState({});
  const [definitions, setDefinitions] = useState({});
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  
  // Neue Rolle Form
  const [showNewRoleForm, setShowNewRoleForm] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleColor, setNewRoleColor] = useState("#6366f1");
  const [editingRole, setEditingRole] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const PRESET_COLORS = [
    "#ef4444", "#f97316", "#eab308", "#22c55e", "#10b981",
    "#14b8a6", "#06b6d4", "#3b82f6", "#6366f1", "#8b5cf6",
    "#a855f7", "#d946ef", "#ec4899", "#f43f5e", "#64748b"
  ];

  function authHeaders() {
    return authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : {};
  }

  async function loadData() {
    try {
      const [permRes, rolesRes] = await Promise.all([
        axios.get(`${API_BASE}/api/permissions`, authHeaders()),
        axios.get(`${API_BASE}/api/roles`, authHeaders())
      ]);
      setPermissions(permRes.data.permissions || {});
      setDefinitions(permRes.data.definitions || {});
      setRoles(rolesRes.data || []);
    } catch (e) {
      console.error(e);
      toast({ title: "Fehler beim Laden", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, [authToken]);

  const togglePermission = async (role, permKey) => {
    const currentValue = permissions[role]?.[permKey] ?? false;
    const newValue = !currentValue;

    setPermissions((prev) => ({
      ...prev,
      [role]: { ...prev[role], [permKey]: newValue },
    }));

    setSaving(`${role}-${permKey}`);
    try {
      await axios.put(
        `${API_BASE}/api/permissions/${role}`,
        { permissions: { [permKey]: newValue } },
        authHeaders()
      );
    } catch (e) {
      console.error(e);
      setPermissions((prev) => ({
        ...prev,
        [role]: { ...prev[role], [permKey]: currentValue },
      }));
      toast({ title: "Fehler beim Speichern", variant: "destructive" });
    } finally {
      setSaving(null);
    }
  };

  const resetRole = async (roleId) => {
    const roleName = roles.find(r => r.id === roleId)?.name || roleId;
    if (!window.confirm(`Berechtigungen für "${roleName}" auf Standard zurücksetzen?`)) {
      return;
    }
    try {
      const res = await axios.post(`${API_BASE}/api/permissions/reset/${roleId}`, {}, authHeaders());
      setPermissions((prev) => ({ ...prev, [roleId]: res.data.permissions }));
      toast({ title: "Berechtigungen zurückgesetzt" });
    } catch (e) {
      console.error(e);
      toast({ title: "Fehler beim Zurücksetzen", variant: "destructive" });
    }
  };

  const createRole = async () => {
    if (!newRoleName.trim()) {
      toast({ title: "Bitte Namen eingeben", variant: "destructive" });
      return;
    }
    try {
      const res = await axios.post(`${API_BASE}/api/roles`, {
        name: newRoleName.trim(),
        color: newRoleColor
      }, authHeaders());
      
      setRoles((prev) => [...prev, res.data]);
      setPermissions((prev) => ({
        ...prev,
        [res.data.id]: Object.fromEntries(Object.keys(definitions).map(k => [k, false]))
      }));
      
      setNewRoleName("");
      setNewRoleColor("#6366f1");
      setShowNewRoleForm(false);
      toast({ title: `Rolle "${res.data.name}" angelegt` });
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Fehler beim Anlegen";
      toast({ title: msg, variant: "destructive" });
    }
  };

  const updateRole = async () => {
    if (!editingRole) return;
    try {
      const res = await axios.put(`${API_BASE}/api/roles/${editingRole.id}`, {
        name: editingRole.name,
        color: editingRole.color
      }, authHeaders());
      
      setRoles((prev) => prev.map(r => r.id === editingRole.id ? res.data : r));
      setEditingRole(null);
      toast({ title: "Rolle aktualisiert" });
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Fehler beim Speichern";
      toast({ title: msg, variant: "destructive" });
    }
  };

  const deleteRole = async (roleId) => {
    try {
      await axios.delete(`${API_BASE}/api/roles/${roleId}`, authHeaders());
      setRoles((prev) => prev.filter(r => r.id !== roleId));
      const newPerms = { ...permissions };
      delete newPerms[roleId];
      setPermissions(newPerms);
      setDeleteConfirm(null);
      toast({ title: "Rolle gelöscht" });
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Fehler beim Löschen";
      toast({ title: msg, variant: "destructive" });
    }
  };

  const groupedPermissions = useMemo(() => {
    const groups = {};
    Object.entries(definitions).forEach(([key, def]) => {
      const cat = def.category || "Sonstiges";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push({ key, ...def });
    });
    return groups;
  }, [definitions]);

  const getTextColor = (bgColor) => {
    if (!bgColor?.startsWith("#")) return "#ffffff";
    try {
      const r = parseInt(bgColor.slice(1, 3), 16);
      const g = parseInt(bgColor.slice(3, 5), 16);
      const b = parseInt(bgColor.slice(5, 7), 16);
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? "#1f2937" : "#ffffff";
    } catch {
      return "#ffffff";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-violet-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="role-permissions-matrix">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl bg-white p-4 shadow-sm">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Rollen & Berechtigungen</h3>
          <p className="text-sm text-slate-500">
            {roles.length} Rollen · Super-Admin hat automatisch alle Rechte
          </p>
        </div>
        <button
          data-testid="new-role-button"
          onClick={() => setShowNewRoleForm(!showNewRoleForm)}
          className={`rounded-full px-4 py-2 text-sm font-medium transition-all ${
            showNewRoleForm ? "bg-slate-900 text-white" : "bg-violet-100 text-violet-700 hover:bg-violet-200"
          }`}
        >
          {showNewRoleForm ? "Abbrechen" : "+ Neue Rolle"}
        </button>
      </div>

      {/* Neue Rolle Form */}
      {showNewRoleForm && (
        <div className="rounded-xl bg-gradient-to-br from-violet-50 to-white p-4 shadow-sm">
          <h4 className="mb-3 text-sm font-semibold text-violet-900">Neue Rolle anlegen</h4>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="mb-1 block text-xs text-slate-500">Rollenname</label>
              <input
                type="text"
                data-testid="new-role-name"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                placeholder="z.B. Kassenwart"
                className="w-full rounded-lg border-0 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-500">Farbe</label>
              <div className="flex gap-1">
                {PRESET_COLORS.slice(0, 8).map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setNewRoleColor(color)}
                    className={`h-8 w-8 rounded-lg transition-all ${newRoleColor === color ? "ring-2 ring-offset-1 ring-slate-400" : ""}`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
            <button
              data-testid="create-role-button"
              onClick={createRole}
              className="rounded-full bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500"
            >
              Anlegen
            </button>
          </div>
        </div>
      )}

      {/* Lösch-Bestätigung */}
      {deleteConfirm && (
        <div className="rounded-xl bg-rose-50 border border-rose-200 p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-rose-100">
              <svg className="h-5 w-5 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-rose-900">Rolle löschen?</h4>
              <p className="mt-1 text-xs text-rose-700">
                Möchten Sie die Rolle <strong>"{deleteConfirm.name}"</strong> wirklich löschen?
              </p>
              <div className="mt-3 flex gap-2">
                <button onClick={() => setDeleteConfirm(null)} className="rounded-full bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50">
                  Abbrechen
                </button>
                <button onClick={() => deleteRole(deleteConfirm.id)} className="rounded-full bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-500">
                  Ja, löschen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rollen-Verwaltung */}
      <div className="rounded-xl bg-white p-4 shadow-sm">
        <h4 className="mb-3 text-sm font-semibold text-slate-700">Rollen</h4>
        <div className="flex flex-wrap gap-2">
          {roles.map((role) => (
            <div
              key={role.id}
              data-testid={`role-chip-${role.id}`}
              className="group flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium"
              style={{ backgroundColor: role.color, color: getTextColor(role.color) }}
            >
              {editingRole?.id === role.id ? (
                <>
                  <input
                    type="text"
                    value={editingRole.name}
                    onChange={(e) => setEditingRole({ ...editingRole, name: e.target.value })}
                    className="w-24 rounded bg-white/20 px-1 text-sm outline-none"
                    autoFocus
                  />
                  <button onClick={updateRole} className="opacity-80 hover:opacity-100">✓</button>
                  <button onClick={() => setEditingRole(null)} className="opacity-80 hover:opacity-100">✕</button>
                </>
              ) : (
                <>
                  <span>{role.name}</span>
                  {role.is_system ? (
                    <span className="rounded bg-white/20 px-1 text-[10px]">System</span>
                  ) : (
                    <div className="hidden group-hover:flex items-center gap-1">
                      <button
                        onClick={() => setEditingRole(role)}
                        className="opacity-70 hover:opacity-100"
                        title="Bearbeiten"
                      >
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(role)}
                        className="opacity-70 hover:opacity-100"
                        title="Löschen"
                      >
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Berechtigungs-Matrix */}
      <div className="rounded-xl bg-white shadow-sm">
        {/* Sticky Header - fixiert beim Scrollen der Seite */}
        <div className="sticky top-0 z-20 bg-white rounded-t-xl border-b border-slate-200 shadow-sm">
          <div className="overflow-x-auto">
            <div className="flex min-w-max">
              <div className="flex-shrink-0 w-48 sm:w-56 md:w-64 px-3 sm:px-4 py-3 text-left" style={{ fontSize: 'var(--font-small-size)', fontWeight: 'var(--font-body-weight)' }}>
                <span className="text-slate-500 uppercase tracking-wider">Funktion</span>
              </div>
              {roles.map((role) => (
                <div key={role.id} className="flex-shrink-0 w-20 sm:w-24 md:w-28 px-2 sm:px-3 py-3 text-center">
                  <div className="flex flex-col items-center gap-1">
                    <span
                      className="inline-block h-3 w-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: role.color }}
                    />
                    <span 
                      className="text-slate-700 truncate max-w-full" 
                      style={{ fontSize: 'var(--font-small-size)', fontWeight: 'var(--font-body-weight)' }}
                      title={role.name}
                    >
                      {role.name}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tabellen-Inhalt */}
        <div className="overflow-x-auto">
          <div className="min-w-max">
            {Object.entries(groupedPermissions).map(([category, perms]) => (
              <div key={category}>
                {/* Kategorie-Header als H3 */}
                <div className="bg-slate-50 px-3 sm:px-4 py-2.5">
                  <h3 
                    className="text-slate-700 uppercase tracking-wider"
                    style={{ 
                      fontSize: 'var(--font-h3-size)', 
                      fontWeight: 'var(--font-h3-weight)',
                      fontFamily: 'var(--font-heading)'
                    }}
                  >
                    {category}
                  </h3>
                </div>
                {perms.map((perm, permIdx) => (
                  <div 
                    key={perm.key} 
                    className={`flex items-center border-b border-slate-50 ${permIdx % 2 === 0 ? "bg-white" : "bg-slate-25"}`}
                  >
                    <div 
                      className="flex-shrink-0 w-48 sm:w-56 md:w-64 px-3 sm:px-4 py-2.5 text-slate-700"
                      style={{ fontSize: 'var(--font-body-size)', fontFamily: 'var(--font-primary)' }}
                    >
                      {perm.label}
                    </div>
                    {roles.map((role) => {
                      const isEnabled = permissions[role.id]?.[perm.key] ?? false;
                      const isSaving = saving === `${role.id}-${perm.key}`;
                      return (
                        <div key={role.id} className="flex-shrink-0 w-20 sm:w-24 md:w-28 px-2 sm:px-3 py-2.5 flex justify-center">
                          <button
                            data-testid={`perm-${role.id}-${perm.key}`}
                            onClick={() => togglePermission(role.id, perm.key)}
                            disabled={isSaving}
                            className={`relative inline-flex h-5 w-9 sm:h-6 sm:w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                              isEnabled ? "bg-violet-600" : "bg-slate-200"
                            } ${isSaving ? "opacity-50" : ""}`}
                            aria-label={`${perm.label} für ${role.name}`}
                          >
                            <span
                              className={`inline-block h-3.5 w-3.5 sm:h-4 sm:w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                                isEnabled ? "translate-x-4 sm:translate-x-6" : "translate-x-1"
                              }`}
                            />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Reset-Buttons */}
      <div className="flex flex-wrap gap-2 rounded-xl bg-slate-50 p-3 sm:p-4">
        <span 
          className="text-slate-500 self-center mr-2"
          style={{ fontSize: 'var(--font-small-size)' }}
        >
          Auf Standard zurücksetzen:
        </span>
        {roles.map((role) => (
          <button
            key={role.id}
            onClick={() => resetRole(role.id)}
            className="rounded-full bg-white px-2.5 sm:px-3 py-1 sm:py-1.5 shadow-sm hover:bg-slate-100 whitespace-nowrap"
            style={{ color: role.color, fontSize: 'var(--font-small-size)', fontWeight: 'var(--font-body-weight)' }}
          >
            {role.name}
          </button>
        ))}
      </div>
    </div>
  );
}
